import React, { useCallback, useEffect, useState, useRef, Component } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { MapContainer, TileLayer, Polygon, Marker, Popup, Tooltip, Pane, useMap, useMapEvents } from 'react-leaflet';
import { Truck, Info, Search, Loader2, Navigation, ChevronLeft, ChevronRight, X } from 'lucide-react';
import L from 'leaflet';
// Fix for default marker icon in react-leaflet
delete (L.Icon.Default.prototype as any)._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png'
});
import { DELIVERY_CENTER, DELIVERY_ZONES as zones, getZoneForPosition } from '../data/deliveryZones';
import { Skeleton } from '../components/Skeleton';
const CENTER: [number, number] = DELIVERY_CENTER;
const MAP_CENTER: [number, number] = DELIVERY_CENTER;
// Haversine formula to calculate distance in miles
function getDistanceMiles(lat1: number, lon1: number, lat2: number, lon2: number): number {
  const R = 3958.8; // Earth radius in miles
  const dLat = (lat2 - lat1) * Math.PI / 180;
  const dLon = (lon2 - lon1) * Math.PI / 180;
  const a = Math.sin(dLat / 2) * Math.sin(dLat / 2) + Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) * Math.sin(dLon / 2) * Math.sin(dLon / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c;
}
// Component to handle map clicks outside zones
function MapClickHandler({
  center
}: {
  center: [number, number];
}) {
  const map = useMapEvents({
    click(e) {
      const zoneInfo = getZoneForPosition(e.latlng.lat, e.latlng.lng);
      if (zoneInfo.zone === 0) {
        L.popup({
          className: 'zone-popup-dark'
        }).setLatLng(e.latlng).setContent('<div style="text-align:center;padding:4px 8px;"><p style="font-weight:bold;color:#ffffff;margin:0 0 4px;">Outside Delivery Zones</p><p style="margin:0;font-size:14px;"><a href="tel:206-619-3742" style="color:#F87431;font-weight:bold;text-decoration:none;">📞 Call for Quote</a></p><p style="margin:4px 0 0;font-size:12px;color:#bbbbbb;">(206) 619-3742</p></div>').openOn(map);
      }
    }
  });
  return null;
}
// Component to fly the map to a location
function FlyTo({
  position
}: {
  position: [number, number] | null;
}) {
  const map = useMap();
  useEffect(() => {
    if (position) {
      map.flyTo(position, 11, {
        duration: 1.5
      });
    }
  }, [position, map]);
  return null;
}
// Component to force Leaflet to recompute its size. Uses a ResizeObserver
// so any container resize (page transition finishing, font load, layout
// shift, mobile keyboard, etc.) triggers an invalidateSize. We also
// invalidate every animation frame for the first ~500ms to catch the
// initial mount-through-animation window.
function MapInvalidator() {
  const map = useMap();
  useEffect(() => {
    const container = map.getContainer();
    const invalidate = () => map.invalidateSize({
      pan: false
    });
    // 1. ResizeObserver — fires whenever container dimensions change
    const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(() => invalidate()) : null;
    if (ro) ro.observe(container);
    // 2. RAF loop — covers PageTransition animation window
    let frames = 0;
    let raf = requestAnimationFrame(function loop() {
      invalidate();
      if (frames++ < 30) raf = requestAnimationFrame(loop);
    });
    // 3. Backup timeouts
    const timeouts = [200, 600, 1500].map(ms => setTimeout(invalidate, ms));
    // 4. Window resize (orientation change, etc.)
    window.addEventListener('resize', invalidate);
    return () => {
      ro?.disconnect();
      cancelAnimationFrame(raf);
      timeouts.forEach(clearTimeout);
      window.removeEventListener('resize', invalidate);
    };
  }, [map]);
  return null;
}
// Ping data for random purchases
const PING_MATERIALS: {
  name: string;
  maxYards: number;
}[] = [{
  name: '5/8" Minus Gravel',
  maxYards: 12
}, {
  name: 'Pea Gravel',
  maxYards: 12
}, {
  name: '7/8" Drain Rock',
  maxYards: 12
}, {
  name: '3-Way Topsoil',
  maxYards: 15
}, {
  name: 'Medium Bark',
  maxYards: 15
}, {
  name: 'Coarse Bark',
  maxYards: 15
}, {
  name: '1.25" Minus',
  maxYards: 12
}, {
  name: 'Road Base',
  maxYards: 12
}, {
  name: 'Builders Sand',
  maxYards: 12
}, {
  name: 'Fill Dirt',
  maxYards: 15
}, {
  name: '2-4" Spalls',
  maxYards: 12
}];
const YARD_OPTIONS = [12, 15];
function getRandomQuantity(maxYards: number): string {
  const valid = YARD_OPTIONS.filter(y => y <= maxYards);
  return `${valid[Math.floor(Math.random() * valid.length)]} yards`;
}
function randomDateRecent(): string {
  const daysAgo = Math.floor(Math.random() * 7);
  if (daysAgo === 0) return 'Today';
  if (daysAgo === 1) return 'Yesterday';
  return `${daysAgo} days ago`;
}
type PingData = {
  id: number;
  latLng: [number, number];
  material: string;
  quantity: string;
  date: string;
  createdAt: number;
};
// Radar Overlay Component
function RadarOverlay({
  center,
  isVisible
}: {
  center: [number, number];
  isVisible: boolean;
}) {
  const map = useMap();
  const [position, setPosition] = useState<{
    x: number;
    y: number;
  } | null>(null);
  const [pings, setPings] = useState<PingData[]>([]);
  const pingIdRef = useRef(0);
  useEffect(() => {
    if (!isVisible) return;
    const updatePosition = () => {
      const point = map.latLngToContainerPoint(center);
      setPosition({
        x: point.x,
        y: point.y
      });
    };
    updatePosition();
    map.on('move', updatePosition);
    map.on('zoom', updatePosition);
    return () => {
      map.off('move', updatePosition);
      map.off('zoom', updatePosition);
    };
  }, [map, center, isVisible]);
  const [size, setSize] = useState(120);
  useEffect(() => {
    if (!isVisible) return;
    const updateSize = () => {
      const centerPoint = map.latLngToContainerPoint(center);
      // 50 miles radius — reaches the outer edges of Zone 5
      const edgeLat = center[0] + 50 * 1.60934 / 111.32;
      const edgePoint = map.latLngToContainerPoint([edgeLat, center[1]]);
      const pixelRadius = Math.abs(centerPoint.y - edgePoint.y);
      setSize(pixelRadius * 2);
    };
    updateSize();
    map.on('zoom', updateSize);
    map.on('zoomend', updateSize);
    return () => {
      map.off('zoom', updateSize);
      map.off('zoomend', updateSize);
    };
  }, [map, center, isVisible]);
  // Track radar angle and spawn pings when the sweep line crosses a queued position
  const radarStartRef = useRef(Date.now());
  const RADAR_DURATION = 13500; // matches CSS animation duration in ms
  const nextPingAngleRef = useRef<number>(Math.random() * 360);
  const lastSpawnAngleRef = useRef<number>(-1);
  useEffect(() => {
    if (!isVisible) return;
    radarStartRef.current = Date.now();
    const zoneRanges: [number, number][] = [[0, 10], [10, 18], [18, 28], [28, 40], [40, 50]];
    // Beyond-zone-5 cities — occasionally appear in the rotation to prove
    // we deliver past the outer zone. They cut in/out with the radar like
    // every other ping (no permanent pins).
    const beyondCities = [{
      latLng: [47.0379, -122.9007] as [number, number],
      name: 'Olympia'
    }, {
      latLng: [47.9789, -122.2021] as [number, number],
      name: 'Everett'
    }, {
      latLng: [48.0518, -122.1771] as [number, number],
      name: 'Marysville'
    }];
    setPings([]);
    // Queue the first target angle
    nextPingAngleRef.current = Math.random() * 360;
    lastSpawnAngleRef.current = -1;
    const tick = setInterval(() => {
      const elapsed = (Date.now() - radarStartRef.current) % RADAR_DURATION;
      const currentAngle = elapsed / RADAR_DURATION * 360;
      const target = nextPingAngleRef.current;
      // Check if the radar arm just crossed the target angle
      const prev = lastSpawnAngleRef.current;
      const crossed = prev >= 0 && (prev < target && currentAngle >= target || prev > currentAngle && (prev < target || currentAngle >= target));
      lastSpawnAngleRef.current = currentAngle;
      if (crossed) {
        const mat = PING_MATERIALS[Math.floor(Math.random() * PING_MATERIALS.length)];
        // ~12% chance to surface a beyond-zone-5 long-haul delivery
        const useBeyond = Math.random() < 0.12;
        let pingLatLng: [number, number];
        if (useBeyond) {
          pingLatLng = beyondCities[Math.floor(Math.random() * beyondCities.length)].latLng;
        } else {
          // Spawn ping at the target angle, random zone
          const zoneRange = zoneRanges[Math.floor(Math.random() * zoneRanges.length)];
          const dist = zoneRange[0] + Math.random() * (zoneRange[1] - zoneRange[0]);
          const distKm = dist * 1.60934;
          const angleRad = target * Math.PI / 180;
          const lat = center[0] + distKm / 111.32 * Math.cos(angleRad);
          const lng = center[1] + distKm / (111.32 * Math.cos(center[0] * (Math.PI / 180))) * Math.sin(angleRad);
          pingLatLng = [lat, lng];
        }
        const newPing: PingData = {
          id: pingIdRef.current++,
          latLng: pingLatLng,
          material: mat.name,
          quantity: getRandomQuantity(mat.maxYards),
          date: randomDateRecent(),
          createdAt: Date.now()
        };
        setPings(prev => [...prev.slice(-9), newPing]);
        // Queue next ping at a random angle offset (60-240 degrees ahead)
        nextPingAngleRef.current = (target + 60 + Math.random() * 180) % 360;
      }
    }, 100);
    return () => clearInterval(tick);
  }, [center, isVisible]);
  // Remove expired pings (older than 5 seconds)
  useEffect(() => {
    if (pings.length === 0) return;
    const timer = setInterval(() => {
      setPings(prev => prev.filter(p => Date.now() - p.createdAt < 5000));
    }, 500);
    return () => clearInterval(timer);
  }, [pings.length]);
  if (!isVisible || !position) return null;
  return <>
      {/* Radar sweep */}
      <div className="absolute pointer-events-none z-[400]" style={{
      left: position.x - size / 2,
      top: position.y - size / 2,
      width: size,
      height: size
    }} data-id="element-1952">
        <div className="relative w-full h-full rounded-[50%] bg-brand-orange/[0.02]" data-id="element-1953">
          <div className="radar-sweep" data-id="element-1954" />
          <div className="absolute top-1/2 left-1/2 w-2 h-2 -mt-1 -ml-1 bg-brand-orange/50 rounded-full shadow-[0_0_4px_rgba(5,137,77,0.4)]" data-id="element-1955" />
        </div>
      </div>

      {/* Purchase pings — cut in / cut out, no fade */}
      {pings.map(ping => {
      const point = map.latLngToContainerPoint(ping.latLng);
      return <div key={ping.id} className="absolute pointer-events-none z-[450]" style={{
        left: point.x,
        top: point.y,
        transform: 'translate(-50%, -100%)'
      }} data-id="element-1956">
            {/* Ping dot */}
            <div className="absolute bottom-0 left-1/2 -translate-x-1/2 translate-y-1/2 w-2 h-2 bg-brand-orange rounded-full shadow-[0_0_6px_rgba(5,137,77,0.6)]" data-id="element-1957" />
            {/* Info card */}
            <div className="mb-2 bg-black/70 backdrop-blur-md border border-white/10 rounded-lg px-3 py-2 whitespace-nowrap shadow-xl" data-id="element-1958">
              <p className="text-white text-xs font-semibold" data-id="element-1959">
                {ping.quantity} — {ping.material}
              </p>
              <p className="text-[10px]" style={{
            color: '#808080'
          }} data-id="element-1960">
                Purchased {ping.date}
              </p>
            </div>
          </div>;
    })}
    </>;
}
// Custom pulsating red marker for user location
const pulsatingIcon = L.divIcon({
  className: 'custom-div-icon',
  html: '<div class="pulsating-pin"><div class="pulsating-pin-dot"></div></div>',
  iconSize: [20, 20],
  iconAnchor: [10, 10],
  popupAnchor: [0, -15]
});
// Heuristic: looks like a real street address (number + street name)
function isAddressLikelyValid(addr: string): boolean {
  const trimmed = addr.trim();
  if (trimmed.length < 8) return false;
  // Must contain a house number (1+ digits)
  if (!/\d/.test(trimmed)) return false;
  // Must contain a street name word with at least 3 letters
  const letterWords = trimmed.match(/[A-Za-z]{3,}/g);
  if (!letterWords || letterWords.length < 1) return false;
  // Must have at least 2 space-separated tokens (number + name)
  const tokens = trimmed.split(/\s+/).filter(Boolean);
  if (tokens.length < 2) return false;
  return true;
}
export function DeliveryZones() {
  const [address, setAddress] = useState('');
  const [isSearching, setIsSearching] = useState(false);
  const [userPosition, setUserPosition] = useState<[number, number] | null>(null);
  const [searchResult, setSearchResult] = useState<{
    zone: number;
    price: string;
    color: string;
    distance: number;
    displayName: string;
  } | null>(null);
  const [searchError, setSearchError] = useState('');
  const hasAutoSearched = useRef(false);
  const [mapLoaded, setMapLoaded] = useState(false);
  const [isLegendOpen, setIsLegendOpen] = useState(false);
  const addressIsValid = isAddressLikelyValid(address);
  const handleSearch = useCallback(async () => {
    if (!address.trim()) return;
    if (!isAddressLikelyValid(address)) {
      setSearchError('Please enter a complete address — house number and street name (e.g. "123 Main St, Tacoma").');
      setSearchResult(null);
      setUserPosition(null);
      return;
    }
    setIsSearching(true);
    setSearchError('');
    setSearchResult(null);
    setUserPosition(null);
    try {
      const query = encodeURIComponent(address + ', WA');
      const res = await fetch(`https://nominatim.openstreetmap.org/search?format=json&q=${query}&limit=1&countrycodes=us&addressdetails=1`);
      const data = await res.json();
      if (!data || data.length === 0) {
        setSearchError('Address not found. Please try a more specific address.');
        setIsSearching(false);
        return;
      }
      const result = data[0];
      const lat = parseFloat(result.lat);
      const lon = parseFloat(result.lon);
      const distance = getDistanceMiles(CENTER[0], CENTER[1], lat, lon);
      // Sanity check 1: must be in WA
      const state = result.address?.state;
      if (state && state !== 'Washington') {
        setSearchError('That address is outside Washington. Please double-check your input.');
        setIsSearching(false);
        return;
      }
      // Sanity check 2: must be reasonably close (within 100 miles of our center)
      if (distance > 100) {
        setSearchError('We couldn\'t find that address near our delivery area. Please add a city (e.g. "123 Main St, Tacoma").');
        setIsSearching(false);
        return;
      }
      // Sanity check 3: result must be specific enough — not just a city or region
      const acceptableTypes = ['house', 'building', 'residential', 'address', 'place', 'yes'];
      const okType = !result.type || acceptableTypes.includes(result.type) || result.class === 'building' || result.class === 'place';
      const hasHouseNumber = result.address?.house_number || /^\d+\s/.test(result.display_name || '');
      if (!hasHouseNumber && !okType) {
        setSearchError('That looks like a general area rather than a specific address. Please include a house number and street.');
        setIsSearching(false);
        return;
      }
      const zoneInfo = getZoneForPosition(lat, lon);
      setUserPosition([lat, lon]);
      setSearchResult({
        ...zoneInfo,
        distance: Math.round(distance * 10) / 10,
        displayName: result.display_name
      });
    } catch {
      setSearchError('Something went wrong. Please try again.');
    }
    setIsSearching(false);
  }, [address]);
  // Auto-search if address is passed via URL query param
  useEffect(() => {
    if (hasAutoSearched.current) return;
    // With HashRouter, query params are inside the hash: /#/delivery-zones?address=...
    const hash = window.location.hash; // e.g. "#/delivery-zones?address=123+Main"
    const qIndex = hash.indexOf('?');
    const params = qIndex >= 0 ? new URLSearchParams(hash.slice(qIndex)) : new URLSearchParams();
    const urlAddress = params.get('address');
    if (urlAddress) {
      setAddress(urlAddress);
      hasAutoSearched.current = true;
    }
  }, []);
  // Trigger search when address is set from URL param
  useEffect(() => {
    if (hasAutoSearched.current && address && !searchResult && !isSearching && !searchError) {
      handleSearch();
    }
  }, [address, hasAutoSearched.current, searchResult, isSearching, searchError, handleSearch]);
  return <main className="relative w-full min-h-[calc(100vh-64px)] flex flex-col lg:block bg-[#373737]" data-id="element-1961">
      {/* Single Map Container - Works at all screen sizes */}
      <div className="h-[50vh] lg:h-auto lg:absolute lg:inset-0 z-0 relative" data-id="element-1962">
        {!mapLoaded && <Skeleton className="absolute inset-0 z-10" data-id="element-1963" />}
        <MapContainer center={MAP_CENTER} zoom={10} scrollWheelZoom={true} className="w-full h-full" zoomControl={false} whenReady={() => setMapLoaded(true)} data-id="element-1964">
          <TileLayer attribution="&copy; Google Maps" url="https://mt1.google.com/vt/lyrs=m&x={x}&y={y}&z={z}" data-id="element-1965" />
          <FlyTo position={userPosition} data-id="element-1966" />
          <MapInvalidator data-id="element-1967" />
          <MapClickHandler center={CENTER} data-id="element-1968" />

          <RadarOverlay center={CENTER} isVisible={!userPosition} data-id="element-1969" />

          {/* Unified visible overlay — all zones merge into one shape, one color, one opacity */}
          <Pane name="unified-zone-pane" style={{
          opacity: 0.12,
          pointerEvents: 'none',
          zIndex: 390
        }} data-id="element-1970">
            {[...zones].reverse().map(zone => zone.polygons.map((polygonCoords, idx) => <Polygon key={`unified-${zone.id}-${idx}`} positions={polygonCoords as [number, number][]} pane="unified-zone-pane" pathOptions={{
            stroke: false,
            fillColor: '#3b82f6',
            fillOpacity: 1,
            interactive: false
          }} data-id="element-1971" />))}
          </Pane>

          {/* Invisible interactive layer — preserves per-zone popups on click */}
          {[...zones].reverse().map(zone => zone.polygons.map((polygonCoords, idx) => <Polygon key={`${zone.id}-${idx}`} positions={polygonCoords as [number, number][]} pathOptions={{
          stroke: false,
          fillColor: '#000',
          fillOpacity: 0.01,
          interactive: true
        }} data-id="element-1972">
                <Popup className="zone-popup-dark" data-id="element-1973">
                  <div className="text-center" data-id="element-1974">
                    <p className="font-bold text-white" data-id="element-1975">Zone {zone.id}</p>
                    <p className="font-bold text-brand-orange mt-1" data-id="element-1976">
                      Delivery: {zone.price}
                    </p>
                  </div>
                </Popup>
              </Polygon>))}

          {userPosition && <Marker position={userPosition} icon={pulsatingIcon} data-id="element-1977">
              {searchResult && <Tooltip permanent direction="right" offset={[12, 0]} className="zone-speech-bubble" data-id="element-1978">
                  <div className="flex items-center gap-2" data-id="element-1979">
                    <span className="font-bold text-white text-sm" data-id="element-1980">
                      {searchResult.zone > 0 ? `Zone ${searchResult.zone}` : 'Outside Zones'}
                    </span>
                    <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-white/15 text-white/90" data-id="element-1981">
                      {searchResult.zone > 0 ? searchResult.price === 'FREE' ? 'Free Delivery' : `Delivery: ${searchResult.price}` : 'Call for Quote'}
                    </span>
                  </div>
                </Tooltip>}
              <Popup data-id="element-1982">
                <div className="text-center" data-id="element-1983">
                  <p className="font-bold text-brand-charcoal" data-id="element-1984">Your Location</p>
                  {searchResult && <p className="text-sm text-stone-600" data-id="element-1985">
                      Zone {searchResult.zone || 'Outside'}
                    </p>}
                </div>
              </Popup>
            </Marker>}
        </MapContainer>
      </div>

      {/* Top Gradient Overlay (Desktop) */}
      <div className="hidden lg:block absolute top-0 left-0 right-0 h-64 bg-gradient-to-b from-[#373737]/90 via-[#373737]/50 to-transparent z-10 pointer-events-none" data-id="element-1986" />

      {/* Floating UI Container */}
      <div className="relative z-20 flex-1 flex flex-col lg:block lg:absolute lg:inset-0 lg:pointer-events-none" data-id="element-1987">
        {/* Header & Search (Top Center) */}
        <div className="w-full max-w-3xl mx-auto px-4 pt-3 lg:pt-16 pointer-events-auto" data-id="element-1988">
          <motion.div initial={{
          opacity: 0,
          y: -20
        }} animate={{
          opacity: 1,
          y: 0
        }} className="text-center mb-3 lg:mb-8" data-id="element-1989">
            <h1 className="font-heading text-2xl lg:text-5xl font-extrabold text-white mb-1 lg:mb-3 tracking-tight drop-shadow-lg" data-id="element-1990">
              Delivery <span className="text-brand-orange" data-id="element-1991">Zones</span>
            </h1>
            <p className="text-sm lg:text-lg text-stone-300 font-medium drop-shadow-md max-w-xl mx-auto font-sub" data-id="element-1992">
              Enter your address to instantly calculate your delivery zone and
              cost.
            </p>
          </motion.div>

          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.1
        }} data-id="element-1993">
            <form onSubmit={e => {
            e.preventDefault();
            handleSearch();
          }} className="flex flex-col sm:flex-row bg-[#474747]/95 backdrop-blur-md border border-brand-orange shadow-2xl rounded-[5px]" data-id="element-1994">
              <div className="flex-1 relative" data-id="element-1995">
                <Navigation className="absolute left-4 lg:left-5 top-1/2 -translate-y-1/2 w-4 h-4 lg:w-5 lg:h-5 text-stone-400" data-id="element-1996" />
                <input type="text" value={address} onChange={e => setAddress(e.target.value)} placeholder="Enter your delivery address..." autoComplete="street-address" className="w-full pl-11 lg:pl-14 pr-4 py-2.5 lg:py-4 bg-transparent text-white placeholder-stone-400 focus:outline-none text-sm lg:text-lg border-none focus:ring-0 transition-colors" data-id="element-1997" />
              </div>
              <button type="submit" disabled={isSearching || !addressIsValid} className="px-5 lg:px-8 py-2.5 lg:py-4 bg-brand-button hover:bg-brand-buttonHover disabled:opacity-50 disabled:cursor-not-allowed text-white font-bold transition-all flex items-center justify-center gap-2 shrink-0 border-l border-brand-orange text-sm lg:text-base" data-id="element-1998">
                {isSearching ? <Loader2 className="w-4 h-4 lg:w-5 lg:h-5 animate-spin" data-id="element-1999" /> : <Search className="w-4 h-4 lg:w-5 lg:h-5" data-id="element-2000" />}
                <span data-id="element-2001">Find Zone</span>
              </button>
            </form>

            {/* Search Result */}
            <AnimatePresence mode="wait" data-id="element-2002">
              {searchError && <motion.div initial={{
              opacity: 0,
              y: -10
            }} animate={{
              opacity: 1,
              y: 0
            }} exit={{
              opacity: 0,
              scale: 0.95
            }} className="mt-4 p-4 bg-red-500/20 backdrop-blur-md border border-red-500/30 rounded-2xl text-red-200 text-center text-sm font-medium" data-id="element-2003">
                  {searchError}
                </motion.div>}
            </AnimatePresence>
          </motion.div>
        </div>

        {/* Legend — mobile inline, desktop collapsible side panel */}
        {(() => {
        const legendBody = <>
              <h2 className="text-lg mb-4 flex items-center font-[600] font-[Inter,_sans-serif] text-[#7C7C7C]" data-id="element-2004">
                <Truck className="w-5 h-5 mr-2 text-brand-orange" data-id="element-2005" />
                Delivery Rates
              </h2>

              <div className="space-y-2" data-id="element-2006">
                {[{
              id: 1,
              color: '#b8b55c',
              price: '+$40'
            }, {
              id: 2,
              color: '#e18251',
              price: '+$75'
            }, {
              id: 3,
              color: '#ba8989',
              price: '+$120'
            }, {
              id: 4,
              color: '#c98ea3',
              price: '+$150'
            }, {
              id: 5,
              color: '#804747',
              price: '+$180'
            }].map(z => <div key={z.id} className="flex items-center justify-between py-2.5 px-3 rounded-lg hover:bg-white/5 transition-colors" data-id="element-2007">
                    <div className="flex items-center" data-id="element-2008">
                      <div className="w-2.5 h-2.5 rounded-full mr-3" style={{
                  backgroundColor: z.color
                }} data-id="element-2009" />
                      <p className="text-stone-200 text-sm font-[400]" data-id="element-2010">
                        Zone {z.id}
                      </p>
                    </div>
                    <span className="text-stone-300 text-xs font-medium" data-id="element-2011">
                      {z.price}
                    </span>
                  </div>)}
              </div>

              <div className="mt-4 pt-4 border-t border-white/10 space-y-3" data-id="element-2012">
                <div className="flex items-start" data-id="element-2013">
                  <Info className="w-4 h-4 text-brand-orange mr-2 shrink-0 mt-0.5" data-id="element-2014" />
                  <div data-id="element-2015">
                    <p className="text-xs text-stone-300" data-id="element-2016">
                      Outside Zone 5? We can still deliver. Call{' '}
                      <a href="tel:206-619-3742" className="text-brand-orange font-bold hover:underline" data-id="element-2017">
                        (206) 619-3742
                      </a>{' '}
                      for a custom quote.
                    </p>
                  </div>
                </div>
                <p className="text-[11px] text-stone-400 italic" data-id="element-2018">
                  *We do not deliver to Vashon, Maury, or Fox Island
                </p>
              </div>
            </>;
        return <>
              {/* Mobile: inline panel above the map content */}
              <div className="lg:hidden w-full p-4 pointer-events-auto" data-id="element-2019">
                <motion.div initial={{
              opacity: 0,
              x: -20
            }} animate={{
              opacity: 1,
              x: 0
            }} transition={{
              delay: 0.3
            }} className="bg-[#474747]/95 backdrop-blur-xl rounded-3xl p-6 border border-white/10 shadow-2xl w-full" data-id="element-2020">
                  {legendBody}
                </motion.div>
              </div>

              {/* Desktop: collapsible slide-out tab anchored to left edge */}
              <motion.div initial={{
            x: -260
          }} animate={{
            x: isLegendOpen ? 0 : -260
          }} transition={{
            type: 'spring',
            stiffness: 260,
            damping: 30
          }} className="hidden lg:flex fixed top-[143px] left-0 z-40 pointer-events-auto items-start" data-id="element-2021">
                {/* Panel */}
                <div className="bg-[#474747]/95 backdrop-blur-xl rounded-r-3xl p-5 border border-l-0 border-white/10 shadow-2xl w-[260px]" data-id="element-2022">
                  {legendBody}
                </div>

                {/* Tab handle — always visible, attached to right edge of panel */}
                <button type="button" onClick={() => setIsLegendOpen(v => !v)} aria-label={isLegendOpen ? 'Collapse rates' : 'Show rates'} aria-expanded={isLegendOpen} className="group bg-[#474747]/95 backdrop-blur-xl border border-l-0 border-white/10 hover:bg-[#5a5a5a]/95 transition-colors rounded-r-2xl shadow-2xl py-6 px-2 flex flex-col items-center gap-3 -ml-px" data-id="element-2023">
                  {isLegendOpen ? <ChevronLeft className="w-4 h-4 text-brand-orange" data-id="element-2024" /> : <ChevronRight className="w-4 h-4 text-brand-orange" data-id="element-2025" />}
                  <span className="text-[10px] font-medium uppercase tracking-[0.2em] text-stone-400" style={{
                writingMode: 'vertical-rl',
                transform: 'rotate(180deg)'
              }} data-id="element-2026">
                    Rates
                  </span>
                  <Truck className="w-4 h-4 text-stone-400 group-hover:text-white transition-colors" data-id="element-2027" />
                </button>
              </motion.div>
            </>;
      })()}
      </div>
    </main>;
}